import { useMsal } from '@azure/msal-react';
import * as Sentry from '@sentry/react';
import { Alert, Button, message, Modal, Typography } from 'antd';
import axios from 'axios';
import React, { useEffect } from 'react';

import { formatDate } from '../../common/datetime';
import { apiUrl } from '../../common/url';
import { useDataCollBlockedStatus } from '../../hooks/useDataCollBlockedStatus';
import { DataCollBlockedStatus } from './types';

interface DataCollBlockedStatusDisplayProps {
  dataCollName?: string;
  dataCollBlockedStatus: DataCollBlockedStatus | undefined;
  setDataCollBlockedStatus: React.Dispatch<React.SetStateAction<DataCollBlockedStatus | undefined>>;
  client: { id: string; name?: string; token: string; permissions: string[] };
  adminRoles?: string[];
  adminToken?: string;
  styles?: React.CSSProperties;
}

const DataCollBlockedStatusDisplay: React.FC<DataCollBlockedStatusDisplayProps> = ({
  dataCollName,
  dataCollBlockedStatus,
  setDataCollBlockedStatus,
  client,
  adminRoles,
  adminToken,
  styles,
}) => {
  const { instance } = useMsal();

  const { updateBlockedStatus } = useDataCollBlockedStatus({
    dataCollName,
    client,
    setDataCollBlockedStatus,
    adminRoles,
    adminToken,
    currentBlockedStatus: dataCollBlockedStatus,
  });

  const getDataCollBlockedStatus = () => {
    console.log('Sending request with dataCollName:', dataCollName);

    if (!dataCollName) {
      console.warn('dataCollName is undefined or empty');
      return;
    }

    axios
      .get(`${apiUrl}/b2zero/${client.id}/get_data_coll_blocked_status`, {
        params: { data_coll_name: dataCollName },
        headers: { Authorization: `Bearer ${client.token}` },
      })
      .then((response) => {
        console.log('Response:', response.data);
        const dataCollBlockedStatusNew = response.data as DataCollBlockedStatus;
        dataCollBlockedStatusNew.blocking_user_is_current_user =
          dataCollBlockedStatusNew.blocking_user_id === instance.getActiveAccount()?.localAccountId;
        setDataCollBlockedStatus(dataCollBlockedStatusNew);
      })
      .catch((error) => {
        console.error('Error details:', error.response?.data);
        message.error('Fehler beim Abrufen des data_coll Blockierungs-Status: ' + error.message, 3);

        Sentry.withScope((scope) => {
          scope.setLevel('warning');
          scope.setExtra(
            'hint',
            `Fehler beim Abrufen des Blockierungs-Status für die Tabelle ${dataCollName}: ` +
              error.message
          );
          Sentry.captureException(error);
        });
        return [];
      });
  };

  const handleUnblock = () => {
    Modal.confirm({
      title: 'Wirklich Freigeben?',
      content: `Möchten Sie die Blockierung der Tabelle ${dataCollName} wirklich aufheben?`,
      onOk: () => {
        updateBlockedStatus(false)
          .then(() => {
            message.success('Die Tabelle wurde erfolgreich zum Bearbeiten freigegeben.');
          })
          .catch((error) => {
            message.error('Fehler beim Freigeben der Tabelle: ' + error.message, 3);
            Sentry.captureException(error);
          });
      },
      okText: 'Ja, freigeben',
      cancelText: 'Abbrechen',
    });
  };

  useEffect(() => {
    if (dataCollName) {
      getDataCollBlockedStatus();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataCollName]);

  return (
    <>
      {dataCollBlockedStatus && dataCollBlockedStatus.isBlocked && (
        <Alert
          message={`Die Bearbeitung der Tabelle ${dataCollName} wird derzeit ${dataCollBlockedStatus.blocking_user_is_current_user ? 'von Ihnen ' : ''} blockiert!`}
          description={
            <Typography.Paragraph>
              {!dataCollBlockedStatus.blocking_user_is_current_user && (
                <>
                  <b>Blockiert von:</b> {dataCollBlockedStatus.blocking_user_name} (
                  {dataCollBlockedStatus.blocking_user_email})
                  <br />
                </>
              )}
              <b>Blockiert seit:</b> {formatDate(dataCollBlockedStatus.blocked_since)}
            </Typography.Paragraph>
          }
          type={dataCollBlockedStatus.blocking_user_is_current_user ? 'info' : 'error'}
          action={
            (dataCollBlockedStatus.blocking_user_is_current_user ||
              adminRoles?.includes('data_admin')) && (
              <Button size="small" danger onClick={handleUnblock}>
                Freigeben
              </Button>
            )
          }
          style={styles}
        />
      )}
    </>
  );
};

export default DataCollBlockedStatusDisplay;
